import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, MapPin, Euro, Briefcase, Users } from 'lucide-react';

import API from '../../constants/api'
// API base via getApiBase()

const contractTypes = [
  { value: 'CDI', label: 'CDI' },
  { value: 'CDD', label: 'CDD' },
  { value: 'Intérim', label: 'Intérim' },
  { value: 'Freelance', label: 'Freelance' },
  { value: 'Apprentissage', label: 'Apprentissage' },
  { value: 'Stage', label: 'Stage' }
];

const statusLabels = {
  brouillon: { label: 'Brouillon', color: 'bg-slate-100 text-slate-800' },
  publiee: { label: 'Publiée', color: 'bg-green-100 text-green-800' },
  expiree: { label: 'Expirée', color: 'bg-yellow-100 text-yellow-800' },
  archivee: { label: 'Archivée', color: 'bg-slate-100 text-slate-800' },
  DRAFT: { label: 'Brouillon', color: 'bg-slate-100 text-slate-800' },
  PUBLISHED: { label: 'Publiée', color: 'bg-green-100 text-green-800' },
  ARCHIVED: { label: 'Archivée', color: 'bg-slate-100 text-slate-800' },
};

export function MyJobOffersPanel() {
  const { token, user, hasModule } = useAuth();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [formData, setFormData] = useState({
    titre: '',
    type_contrat: 'CDI',
    localisation: '',
    remuneration_min: '',
    remuneration_max: '',
    description: '',
    competences: '',
    experience_requise: '',
    status: 'brouillon'
  });

  useEffect(() => {
    if (hasModule('job_offer_publisher') && user?.id) {
      fetchOffers();
    } else {
      setLoading(false);
    }
  }, [token, user?.id, hasModule]);

  const fetchOffers = async () => {
    if (!token || !user?.id) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/posts`, {
        params: { type: 'JOB', authorId: user.id },
        headers: { Authorization: `Bearer ${token}` }
      });

      const offersData = response.data?.data || response.data || [];
      setOffers(Array.isArray(offersData) ? offersData : []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Impossible de charger vos offres');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = {
        ...formData,
        remuneration_min: formData.remuneration_min ? parseFloat(formData.remuneration_min) : null,
        remuneration_max: formData.remuneration_max ? parseFloat(formData.remuneration_max) : null,
        competences: formData.competences.split(',').map(c => c.trim()).filter(Boolean)
      };

      const postData = {
        type: 'JOB',
        title: data.titre,
        description: data.description,
        locationCity: data.localisation,
        contractType: data.type_contrat,
        salaryRange: data.remuneration_min && data.remuneration_max
          ? `${data.remuneration_min}-${data.remuneration_max}`
          : null,
        status: data.status === 'publiee' ? 'PUBLISHED' : 'DRAFT'
      };

      if (editingOffer) {
        await axios.patch(`${API}/posts/${editingOffer.id}`, postData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Offre mise à jour');
      } else {
        await axios.post(`${API}/posts`, postData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Offre créée');
      }

      setDialogOpen(false);
      resetForm();
      fetchOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette offre ?')) return;

    try {
      await axios.delete(`${API}/posts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Offre supprimée');
      fetchOffers();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditDialog = (offer) => {
    setEditingOffer(offer);
    setFormData({
      titre: offer.title ?? offer.titre ?? '',
      type_contrat: offer.contractType ?? offer.type_contrat ?? 'CDI',
      localisation: offer.locationCity ?? offer.localisation ?? '',
      remuneration_min: offer.remuneration_min || '',
      remuneration_max: offer.remuneration_max || '',
      description: offer.description ?? '',
      competences: offer.competences?.join(', ') || '',
      experience_requise: offer.experience_requise ?? '',
      status: offer.status === 'PUBLISHED' ? 'publiee' : offer.status === 'DRAFT' ? 'brouillon' : offer.status ?? 'brouillon'
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingOffer(null);
    setFormData({
      titre: '',
      type_contrat: 'CDI',
      localisation: '',
      remuneration_min: '',
      remuneration_max: '',
      description: '',
      competences: '',
      experience_requise: '',
      status: 'brouillon'
    });
  };

  if (!hasModule('job_offer_publisher')) {
    return (
      <div className="text-center py-12" data-testid="documents-tab-offres-locked">
        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Briefcase className="w-8 h-8 text-slate-400" />
        </div>
        <h2 className="text-xl font-semibold text-slate-900 mb-2">Accès restreint</h2>
        <p className="text-slate-600 mb-4">
          Activez l&apos;espace Pro pour publier des offres d&apos;emploi.
        </p>
        <Link to="/tableau-de-bord/profil">
          <Button>Activer l&apos;espace Pro</Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="documents-tab-offres">
      <div className="flex items-center justify-between mb-6">
        <p className="text-slate-600">Gérez vos annonces de recrutement.</p>

        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="create-offer-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle offre
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingOffer ? 'Modifier l\'offre' : 'Nouvelle offre d\'emploi'}</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="job-form">
              <div>
                <Label className="form-label">Titre du poste *</Label>
                <Input
                  required
                  value={formData.titre}
                  onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                  placeholder="Ex: Plombier qualifié"
                  data-testid="job-titre"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Type de contrat *</Label>
                  <Select value={formData.type_contrat} onValueChange={(v) => setFormData({ ...formData, type_contrat: v })}>
                    <SelectTrigger data-testid="job-contrat">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {contractTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="form-label">Localisation *</Label>
                  <Input
                    required
                    value={formData.localisation}
                    onChange={(e) => setFormData({ ...formData, localisation: e.target.value })}
                    placeholder="Paris"
                    data-testid="job-localisation"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Salaire min (€/mois)</Label>
                  <Input
                    type="number"
                    value={formData.remuneration_min}
                    onChange={(e) => setFormData({ ...formData, remuneration_min: e.target.value })}
                    placeholder="2500"
                    data-testid="job-salaire-min"
                  />
                </div>
                <div>
                  <Label className="form-label">Salaire max (€/mois)</Label>
                  <Input
                    type="number"
                    value={formData.remuneration_max}
                    onChange={(e) => setFormData({ ...formData, remuneration_max: e.target.value })}
                    placeholder="3500"
                    data-testid="job-salaire-max"
                  />
                </div>
              </div>

              <div>
                <Label className="form-label">Description *</Label>
                <Textarea
                  required
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Décrivez le poste, les missions..."
                  data-testid="job-description"
                />
              </div>

              <div>
                <Label className="form-label">Compétences requises</Label>
                <Input
                  value={formData.competences}
                  onChange={(e) => setFormData({ ...formData, competences: e.target.value })}
                  placeholder="Plomberie, Chauffage, Sanitaire (séparés par des virgules)"
                  data-testid="job-competences"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Expérience requise *</Label>
                  <Input
                    required
                    value={formData.experience_requise}
                    onChange={(e) => setFormData({ ...formData, experience_requise: e.target.value })}
                    placeholder="3-5 ans"
                    data-testid="job-experience"
                  />
                </div>
                <div>
                  <Label className="form-label">Statut</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger data-testid="job-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="brouillon">Brouillon</SelectItem>
                      <SelectItem value="publiee">Publiée</SelectItem>
                      <SelectItem value="archivee">Archivée</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" data-testid="job-submit">
                  {editingOffer ? 'Mettre à jour' : 'Créer l\'offre'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="lux-dashboard-card p-6 animate-pulse">
              <div className="h-5 bg-slate-200 rounded w-1/4 mb-4" />
              <div className="h-6 bg-slate-200 rounded w-3/4 mb-2" />
              <div className="h-4 bg-slate-200 rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : offers.length === 0 ? (
        <div className="lux-dashboard-card p-12 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Briefcase className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucune offre</h3>
          <p className="text-slate-600 mb-4">Créez votre première offre d&apos;emploi</p>
          <Button onClick={() => setDialogOpen(true)}>Créer une offre</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {offers.map((offer) => {
            const statusKey = offer.status ?? 'brouillon';
            const status = statusLabels[statusKey] ?? statusLabels.brouillon;
            const title = offer.title ?? offer.titre;
            const location = offer.locationCity ?? offer.localisation;
            const contract = offer.contractType ?? offer.type_contrat;
            const appsCount = offer._count?.applications ?? offer.applications_count ?? 0;

            return (
              <div key={offer.id} className="lux-dashboard-card p-6" data-testid={`offer-${offer.id}`}>
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    {contract && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        {contract}
                      </Badge>
                    )}
                    <Badge className={status.color}>{status.label}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={() => openEditDialog(offer)}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(offer.id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-slate-900 mb-2">{title}</h3>
                <p className="text-slate-600 mb-4 line-clamp-2">{offer.description}</p>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-4 text-slate-500">
                    {location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {location}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {appsCount} candidature{appsCount > 1 ? 's' : ''}
                    </span>
                  </div>
                  {(offer.remuneration_min || offer.remuneration_max) && (
                    <span className="flex items-center gap-1 font-medium text-slate-900">
                      <Euro className="w-4 h-4" />
                      {offer.remuneration_min && offer.remuneration_max
                        ? `${offer.remuneration_min}€ - ${offer.remuneration_max}€`
                        : offer.remuneration_max ? `Jusqu'à ${offer.remuneration_max}€` : `À partir de ${offer.remuneration_min}€`
                      }
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
